import React from "react";
import {
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  ENTITY_TYPES,
  MenuProps,
  DropdownSx,
  multiplierMappings,
  multiplierOptions,
} from "../../../utils";
import { isNull } from "lodash";

const initialCommonDiscount = {
  CoreDiscountPercent: "",
  NonCoreDiscountPercent: "",
  DatingDays: "",
  Multiplier: "",
};

const CustomDateTextField = styled(TextField)(() => ({
  ".MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "black",
  },
}));

export default React.forwardRef(function CommonDiscount(props, ref) {
  const {
    entityType,
    handleCreatePONumbers = () => {},
    handleDeleteDates = () => {},
    disableSave = false,
    handleCommonDiscountSave,
    handleCommonDiscountReset,
    disabled = false,
  } = props;
  const [dateValue, setDateValue] = React.useState(null);
  const [commonDiscount, setCommonDiscount] = React.useState(
    initialCommonDiscount
  );

  React.useImperativeHandle(ref, () => ({
    resetDiscount() {
      setCommonDiscount(initialCommonDiscount);
    },
  }));
  const handleCommonDiscount = (e) => {
    const newValue = e.target.value;
    if (
      e.target.name === "CoreDiscountPercent" ||
      e.target.name === "NonCoreDiscountPercent"
    ) {
      const currentValue = !isNull(newValue) ? Number(newValue) : null;
      if (currentValue > 100 || currentValue < 0 || isNaN(currentValue)) {
        return e.preventDefault();
      }
    }
    if (e.target.name === "DatingDays") {
      const currentValue = !isNull(newValue) ? Number(newValue) : null;
      if (currentValue > 365 || currentValue < 0 || isNaN(currentValue)) {
        return e.preventDefault();
      }
    }
    setCommonDiscount((prevState) => ({
      ...prevState,
      [e.target.name]: newValue,
    }));
  };
  const handleMultiplierChange = (e) => {
    setCommonDiscount((prevState) => ({
      ...prevState,
      Multiplier: e.target.value,
    }));
  };
  const handleSave = () => {
    handleCommonDiscountSave(
      entityType === ENTITY_TYPES.orders ? dateValue : commonDiscount
    );
    setDateValue(null);
  };
  const handleReset = () => {
    setCommonDiscount({
      CoreDiscountPercent: "",
      NonCoreDiscountPercent: "",
      DatingDays: "",
      Multiplier: "",
    });
    handleCommonDiscountReset();
    setDateValue(null);
  };

  return (
    <Grid
      container
      spacing={1}
      sx={{
        marginTop: 5,
        mb: 1,
        display: "flex",
        alignItems: "center",
      }}
    >
      {entityType === ENTITY_TYPES.discount && (
        <Grid item xs={12} sm={12} md={10} lg={10}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={3} md={2.5} lg={2.5}>
              <FormControl
                fullWidth
                size="small"
                margin="dense"
                sx={{ m: 0, p: 0 }}
              >
                <InputLabel id="discount-multiplier">
                  Discount Multiplier
                </InputLabel>
                <Select
                  labelId="discount-multiplier"
                  label="Discount Multiplier"
                  value={commonDiscount.Multiplier}
                  onChange={handleMultiplierChange}
                  MenuProps={MenuProps}
                >
                  {multiplierOptions.map((option) => (
                    <MenuItem sx={DropdownSx} key={option} value={option}>
                      {multiplierMappings[option]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3} md={2.5} lg={2.5}>
              <TextField
                disabled={disabled}
                label="Core Disc %"
                size="small"
                name="CoreDiscountPercent"
                value={commonDiscount.CoreDiscountPercent}
                onChange={handleCommonDiscount}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2.5} lg={2.5}>
              <TextField
                disabled={disabled}
                label="Non-Core Disc %"
                size="small"
                name="NonCoreDiscountPercent"
                value={commonDiscount.NonCoreDiscountPercent}
                onChange={handleCommonDiscount}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2.5} lg={2.5}>
              <TextField
                disabled={disabled}
                label="Dating Days"
                size="small"
                name="DatingDays"
                value={commonDiscount.DatingDays}
                onChange={handleCommonDiscount}
                onKeyDown={(e) => {
                  if (e.key === ".") {
                    e.preventDefault();
                  }
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      {entityType !== ENTITY_TYPES.orders ? (
        <Grid
          item
          xs={12}
          sm={12}
          md={entityType === ENTITY_TYPES.discount ? 2 : 12}
          lg={entityType === ENTITY_TYPES.discount ? 2 : 12}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            variant="outlined"
            sx={{ mr: 2 }}
            onClick={handleReset}
            data-testid="reset-button"
          >
            Reset
          </Button>
          <Button
            variant="contained"
            disabled={disabled || !disableSave}
            onClick={handleSave}
            data-testid="save-button"
          >
            Save
          </Button>
        </Grid>
      ) : (
        <Grid
          container
          xs={12}
          sm={12}
          md={12}
          lg={12}
          spacing={2}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Grid item>
            <Button
              variant="contained"
              onClick={handleCreatePONumbers}
              disabled={disabled}
            >
              Create PO Numbers
            </Button>
          </Grid>
          <Grid item>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                ampm={false}
                value={dateValue}
                onChange={(newValue) => {
                  setDateValue(newValue);
                }}
                label="Release Date & Time"
                renderInput={(params) => (
                  <CustomDateTextField
                    {...params}
                    disabled
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                minDateTime={dayjs().add(1, "h")}
                maxDateTime={dayjs().add(6, "month")}
                OpenPickerButtonProps={{
                  sx: {
                    color: (theme) => theme.palette.primary.main,
                  },
                }}
                disabled={disabled}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!dateValue || disabled}
            >
              Save Dates
            </Button>
          </Grid>
          <Grid item sx={{ pl: 5 }}>
            <Button
              variant="contained"
              onClick={handleDeleteDates}
              disabled={disabled}
            >
              Delete Dates
            </Button>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
});
